import { useMemo, useState } from 'react';
import { UserPlus, UserX } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { allocationsApi } from '@/features/institute/staff/api/allocations.api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import { getNameInitials } from '@/lib/utils';
import type { InstituteClass } from '@/types/instituteClass';

/**
 * Naming who runs each section of one class, without leaving the classes screen.
 *
 * The same arrangement the staff room's allotment screen edits, asked from the
 * other side: that one starts with a teacher and lists their year, this one
 * starts with a class and asks who takes its register. Both write through the
 * same module, so neither can drift from the rules — a section has one class
 * teacher, and a subject in a section has one teacher.
 *
 * Only the teacher is asked for. Somebody who already takes a subject in the
 * section carries the register through it; anybody else is given a
 * class-teacher-only allotment by the server. Subjects are allotted on the staff
 * room's screen, where the rest of a teacher's year is in view.
 *
 * Teachers already running a section elsewhere are left out of the list. One
 * person, one register is the institute's rule; the staff room's own allotment
 * screen is where an exception would be made deliberately.
 *
 * Everything this screen has to say is said **on the screen**. A toast raised
 * while a dialog is open is drawn underneath it — the dialog is in the browser's
 * top layer — so a refusal reported that way is a refusal nobody sees, and the
 * save looks as though it did nothing (see `Toaster`).
 *
 * One section is saved at a time. A single "save everything" button would leave
 * a half-applied set behind the first refusal — one section moved, the next one
 * not — and the reader would have no way to tell which.
 */

export interface AssignClassTeacherModalProps {
  /** Never null: the caller renders this only once a class is chosen, so every
   *  open starts its requests fresh. */
  instituteClass: InstituteClass;
  isOpen: boolean;
  onClose: () => void;
  /** Fires after each saved section, so the roster behind can refresh. */
  onSaved: () => void;
}

/** Who holds a section's register, as this screen currently understands it. */
interface HeldBy {
  fullName: string;
  designation: string | null;
  /** Absent for what the roster arrived with — it names people, not subjects. */
  subjectName: string | null;
}

/** What one section's picker is set to. */
interface SectionDraft {
  teacherUuid: string;
}

/** A row nobody has touched, and what a saved one is put back to. */
const EMPTY_DRAFT: SectionDraft = { teacherUuid: '' };

export function AssignClassTeacherModal({
  instituteClass,
  isOpen,
  onClose,
  onSaved,
}: AssignClassTeacherModalProps) {
  const { data: options, isLoading, error, refetch } = useApiQuery(
    () => allocationsApi.options(),
    [],
  );

  /**
   * Seeded once from the row the table already has, so the modal opens with the
   * arrangement on screen rather than with a second round trip's worth of
   * blank rows. Every save replaces the entry it touched from the server's
   * answer, never from what was sent.
   */
  const [held, setHeld] = useState<Record<string, HeldBy | null>>(() =>
    Object.fromEntries(
      instituteClass.sections.map((section) => {
        const teacher = instituteClass.classTeachers.find(
          (one) => one.sectionName === section.name,
        );

        return [
          section.uuid,
          teacher
            ? { fullName: teacher.fullName, designation: teacher.designation, subjectName: null }
            : null,
        ];
      }),
    ),
  );

  const [draft, setDraft] = useState<Record<string, SectionDraft>>({});
  const [savingSection, setSavingSection] = useState<string | null>(null);
  /** Why the last save of a section was refused, said under that section. */
  const [rowError, setRowError] = useState<Record<string, string | null>>({});

  /**
   * Everybody free to take a register.
   *
   * Somebody already running a section is left out rather than shown and then
   * refused: one person, one register, and a name in the list that cannot be
   * chosen is a worse answer than a shorter list.
   */
  const freeTeachers = useMemo(
    () => (options?.teachers ?? []).filter((teacher) => teacher.classTeacherOf === null),
    [options],
  );

  const teacherOptions: Option[] = useMemo(
    () =>
      freeTeachers.map((teacher) => ({
        value: teacher.uuid,
        label: teacher.designation ? `${teacher.fullName} · ${teacher.designation}` : teacher.fullName,
      })),
    [freeTeachers],
  );

  /** Why the list is as short as it is — an unexplained gap reads as a fault. */
  const teacherHint = useMemo(() => {
    const busy = (options?.teachers.length ?? 0) - freeTeachers.length;

    if (freeTeachers.length === 0) {
      return busy > 0
        ? 'Every teacher is already running a section. Free one up first.'
        : 'No teacher or principal account is active yet';
    }

    if (busy === 0) return undefined;
    return busy === 1
      ? '1 teacher already runs a section and is not listed'
      : `${busy} teachers already run a section and are not listed`;
  }, [options, freeTeachers]);

  /** Sections this class runs *this year*, which is what may be allotted. */
  const sections = useMemo(() => {
    const allotable = new Set(
      options?.classes
        .find((one) => one.uuid === instituteClass.uuid)
        ?.sections.map((section) => section.uuid) ?? [],
    );

    return instituteClass.sections
      .filter((section) => allotable.has(section.uuid))
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [options, instituteClass]);

  function setPicker(sectionUuid: string, patch: Partial<SectionDraft>) {
    setDraft((current) => ({
      ...current,
      [sectionUuid]: { ...EMPTY_DRAFT, ...current[sectionUuid], ...patch },
    }));
    // Last time's refusal was about last time's choice.
    setRowError((current) => ({ ...current, [sectionUuid]: null }));
  }

  /** What went wrong, in the server's words — its refusals name who holds what. */
  function reasonFor(caught: unknown): string {
    return caught instanceof ApiRequestError ? caught.message : 'Please try again.';
  }

  async function assign(sectionUuid: string) {
    const row = draft[sectionUuid];
    if (!row?.teacherUuid) return;

    // A save that follows a refresh: the person chosen a moment ago may have
    // been given a register in the meantime, here or by somebody else.
    if (!freeTeachers.some((one) => one.uuid === row.teacherUuid)) {
      setRowError((current) => ({
        ...current,
        [sectionUuid]: 'That teacher is now running another section. Choose somebody else.',
      }));
      return;
    }

    setSavingSection(sectionUuid);
    setRowError((current) => ({ ...current, [sectionUuid]: null }));

    try {
      const saved = await allocationsApi.setClassTeacher(sectionUuid, {
        staffUuid: row.teacherUuid,
      });

      // Read back from what was actually written, never from the payload sent.
      const classTeacher = saved.find((allocation) => allocation.isClassTeacher);
      setHeld((current) => ({
        ...current,
        [sectionUuid]: classTeacher
          ? {
              fullName: classTeacher.teacher.fullName,
              designation: null,
              // Null when they run the register without a subject here.
              subjectName: classTeacher.subject?.name ?? null,
            }
          : null,
      }));
      setDraft((current) => ({ ...current, [sectionUuid]: EMPTY_DRAFT }));

      // The saved teacher now runs a section, so they must drop out of the
      // other sections' lists — which only a fresh set of options knows.
      refetch();
      onSaved();
    } catch (caught) {
      setRowError((current) => ({ ...current, [sectionUuid]: reasonFor(caught) }));
    } finally {
      setSavingSection(null);
    }
  }

  async function remove(sectionUuid: string) {
    setSavingSection(sectionUuid);
    setRowError((current) => ({ ...current, [sectionUuid]: null }));

    try {
      await allocationsApi.clearClassTeacher(sectionUuid);
      setHeld((current) => ({ ...current, [sectionUuid]: null }));

      // They are free again, and belong back in every section's list.
      refetch();
      onSaved();
    } catch (caught) {
      setRowError((current) => ({ ...current, [sectionUuid]: reasonFor(caught) }));
    } finally {
      setSavingSection(null);
    }
  }

  const hasNothingToAllot = !isLoading && !error && sections.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Class teachers for ${instituteClass.name}`}
      description={
        options ? `One class teacher per section · ${options.academicYear.name}` : undefined
      }
      size="lg"
      footer={
        <Button variant="secondary" size="sm" onClick={onClose} disabled={savingSection !== null}>
          Done
        </Button>
      }
    >
      {isLoading && <SkeletonLines count={5} />}

      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load this screen">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {hasNothingToAllot && (
        <EmptyState
          icon={<UserX className="h-5 w-5" />}
          title="No sections to allot yet"
          description={`${instituteClass.name} has no section running this year, and a class teacher is given a section rather than a class.`}
        />
      )}

      {!isLoading && !error && sections.length > 0 && (
        <div className="flex flex-col gap-4">
          {sections.map((section) => {
            const current = held[section.uuid] ?? null;
            const row = draft[section.uuid];
            const isSaving = savingSection === section.uuid;
            const isBusy = savingSection !== null;
            const canSave = Boolean(row?.teacherUuid) && !isBusy;
            const failure = rowError[section.uuid];

            return (
              <div key={section.uuid} className="card flex flex-col gap-4 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-accent-soft text-2xs font-bold text-accent"
                      aria-hidden
                    >
                      {current ? getNameInitials(current.fullName) : <UserPlus className="h-4 w-4" />}
                    </span>

                    <span className="flex min-w-0 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-[650] text-ink">
                          {instituteClass.name} - {section.name}
                        </span>
                        {current && <Badge tone="accent">Class teacher</Badge>}
                      </span>
                      {current ? (
                        <span className="truncate text-xs font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">
                          {[current.fullName, current.subjectName ? capitalizeWords(current.subjectName) : current.designation]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      ) : (
                        <span className="truncate text-xs text-ink-3 mt-0.5">
                          Nobody has been allotted this section yet
                        </span>
                      )}
                    </span>
                  </div>

                  {current && (
                    <Button
                      variant="dangerSoft"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => void remove(section.uuid)}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <Select
                  label="Teacher"
                  placeholder={current ? `${current.fullName} (Currently assigned)` : 'Choose a teacher'}
                  options={teacherOptions}
                  value={row?.teacherUuid ?? ''}
                  disabled={isBusy || teacherOptions.length === 0}
                  hint={teacherHint}
                  onChange={(event) => setPicker(section.uuid, { teacherUuid: event.target.value })}
                />

                {failure && (
                  <p role="alert" className="text-xs text-crit">
                    {failure}
                  </p>
                )}

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    isLoading={isSaving}
                    disabled={!canSave}
                    leftIcon={<UserPlus className="h-3.5 w-3.5" />}
                    onClick={() => void assign(section.uuid)}
                  >
                    {current ? 'Change class teacher' : 'Assign class teacher'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
